/**
 * 高德地图插件 - 覆盖物工具函数
 *
 * 设计原则：
 * - Marker：每次地址点变化时全量替换（先 remove 旧 Marker，再 add 新 Marker）
 * - Polyline：复用同一实例，只调用 setPath 更新路径，避免频繁 new 触发重绘
 *
 * 图标支持两种形态（由 getIcon 回调返回）：
 *   - string：图片 URL，走 AMap.Icon 路径
 *   - React.ComponentType：本地 SVG 组件，走 content HTML 路径（renderToStaticMarkup）
 *     SVG 图标尺寸统一为 46×48；装/卸见 AMAP_LOAD_UNLOAD_MARKER_OFFSET（尖端对齐）
 */
import { createElement } from 'react'
import type { ComponentType, SVGProps } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { AMAP_FIXED_MARKER_ICONS, AMAP_LOAD_UNLOAD_MARKER_OFFSET, ROUTE_STYLE } from './constants'
import type { AmapLngLatPoint } from './types'

/** 图标支持 URL 字符串或 React SVG 组件 */
type MarkerIcon = string | ComponentType<SVGProps<SVGSVGElement>>

/** 本地 SVG 组件图标的固定尺寸 */
const SVG_ICON_SIZE = { width: 46, height: 48 }

/**
 * 通用 46×48 SVG 默认偏移（非装/卸或未单独配置时使用）。
 * 装/卸请用 resolveSvgMarkerOffset → AMAP_LOAD_UNLOAD_MARKER_OFFSET。
 */
export const AMAP_SVG_ROUTE_MARKER_OFFSET: [number, number] = [
  -SVG_ICON_SIZE.width / 2,
  -SVG_ICON_SIZE.height,
]

/** 按图标类型解析 offset（装/卸可独立配置） */
const resolveSvgMarkerOffset = (icon: MarkerIcon): [number, number] => {
  if (icon === AMAP_FIXED_MARKER_ICONS.start) {
    return AMAP_LOAD_UNLOAD_MARKER_OFFSET.start
  }
  if (icon === AMAP_FIXED_MARKER_ICONS.end) {
    return AMAP_LOAD_UNLOAD_MARKER_OFFSET.end
  }
  return AMAP_SVG_ROUTE_MARKER_OFFSET
}

/**
 * 把 SVG React 组件渲染为 HTML 字符串
 * 浏览器环境下 react-dom/server 可正常打包使用，仅用于轻量静态渲染，无 hydration 逻辑。
 */
const svgComponentToHtml = (
  SvgComponent: ComponentType<SVGProps<SVGSVGElement>>
) => {
  return renderToStaticMarkup(
    createElement(SvgComponent, SVG_ICON_SIZE)
  )
}

/**
 * 自适应视野到所有点
 * 使用 AMap.LngLatBounds 计算包围盒再 setBounds，
 * 不依赖 setFitView 的覆盖物参数，避免「getBounds is not a function」
 * @param map AMap.Map 实例
 * @param points 需要纳入视野的坐标点列表
 */
export const fitMapViewByPoints = (map: any, points: AmapLngLatPoint[]) => {
  if (!map || points.length < 1) {
    return
  }
  const { AMap } = window as any
  if (!AMap?.LngLatBounds) {
    return
  }
  const bounds = new AMap.LngLatBounds()
  points.forEach((p) => bounds.extend([p.lon, p.lat]))
  // immediately=true：与 amap-route-map 程序 fit 一致，减少 moveend/zoomend 晚到误判用户操作
  map.setBounds(bounds, true, [40, 40, 40, 40])
}

/**
 * 同步地址点 Marker（全量替换策略）
 * @param AMap        AMap 命名空间
 * @param map         AMap.Map 实例
 * @param markerCache 上一次的 Marker 列表（用于 remove）
 * @param points      最新地址坐标列表
 * @param getIcon     根据点索引和总数返回图标：URL 字符串 → AMap.Icon；SVG 组件 → content HTML
 * @returns 新的 Marker 列表（存入 ref 供下次 remove 使用）
 */
export const syncRouteMarkers = (
  AMap: any,
  map: any,
  markerCache: any[],
  points: AmapLngLatPoint[],
  getIcon: (index: number, total: number) => MarkerIcon
) => {
  markerCache.forEach((item) => map.remove(item))

  const nextMarkerCache = points.map((point, index) => {
    const icon = getIcon(index, points.length)

    if (typeof icon === 'string') {
      // URL 字符串：走 AMap.Icon，保持原有尺寸约定
      return new AMap.Marker({
        position: [point.lon, point.lat],
        icon: new AMap.Icon({
          size: new AMap.Size(25, 34),
          image: icon,
          imageSize: new AMap.Size(25, 34),
        }),
        offset: new AMap.Pixel(-12, -30),
      })
    }

    // React SVG 组件：渲染为 HTML 字符串，使用 content 注入到 Marker
    const [dx, dy] = resolveSvgMarkerOffset(icon)
    return new AMap.Marker({
      position: [point.lon, point.lat],
      content: svgComponentToHtml(icon),
      offset: new AMap.Pixel(dx, dy),
    })
  })

  if (nextMarkerCache.length) {
    map.add(nextMarkerCache)
  }

  return nextMarkerCache
}

/**
 * 创建或更新路线折线
 * - 首次调用：新建 Polyline 并 add 到地图
 * - 后续调用：复用已有实例，仅 setPath，减少重绘
 * @param AMap             AMap 命名空间
 * @param map              AMap.Map 实例
 * @param previousPolyline 上一次返回的 Polyline 实例（null 表示首次）
 * @param path             新的 GCJ02 折线点列表
 * @returns 当前 Polyline 实例（存入 ref 供下次 setPath 使用）
 */
export const createOrUpdateRoutePolyline = (
  AMap: any,
  map: any,
  previousPolyline: any,
  path: AmapLngLatPoint[]
) => {
  const normalizedPath = path.map((item) => [item.lon, item.lat])
  if (!previousPolyline) {
    const { strokeColor, strokeWidth, outlineWidth, strokeAndOutlineOpacity, outlineColor } = ROUTE_STYLE
    const polyline = new AMap.Polyline({
      path: normalizedPath,
      strokeColor,
      strokeWeight: strokeWidth,
      strokeOpacity: strokeAndOutlineOpacity,
      isOutline: outlineWidth > 0,
      outlineColor,
      borderWeight: outlineWidth,
      lineJoin: 'round',
      lineCap: 'round',
      showDir: true,
    })
    map.add(polyline)
    return polyline
  }

  // 复用实例只更新路径，避免整条折线销毁重建
  previousPolyline.setPath(normalizedPath)
  return previousPolyline
}
